/// <amd-module name="TFS/Admin/Views/OrganizationProjects/OrganizationProjectsTable" />
import React = require("react");

import AdminResources = require("TFS/Admin/Views/OrganizationProjects/Resources");

import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import { SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { OrganizationProjectReference, OrganizationProjectsConstants } from "TFS/Admin/Views/OrganizationProjects/Contracts/DataModels";
import { ProjectVisibility } from "Tfs/Platform/Contracts/Core";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { Components } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import {
    ColumnMore,
    ColumnSelect,
    ColumnSorting,
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    sortItems,
    SortOrder,
    Table,
    TableRow
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { VssPersona } from "VSSUI/VssPersona";

export interface IOrganizationProjectsTableProps extends IFilterablePivotProps, IFilterablePivotOwnProps {
    OnDeleteProject: (project: OrganizationProjectReference) => void;
    OnRenameProject: (project: OrganizationProjectReference) => void;
    OnCreateProject: () => void;
    onMultiProjectDelete: (projects: OrganizationProjectReference[]) => void;
    OnRowClicked: (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<OrganizationProjectReference>) => void;
    OnSearchProject: (projects: OrganizationProjectReference[], newKeyword?: string, oldKeyword?: string, scopeDescriptor?: string) => void;
    Projects: OrganizationProjectReference[];
    IsInProgress?: boolean;
    ClearSelection: ObservableValue<boolean>;
}

interface IOrganizationProjectsTableSimpleCellProps {
    cellKey: string;
    columnIndex: number;
    tableColumn: ITableColumn<OrganizationProjectReference>;
}

function OrganizationProjectsTableSimpleCell(props: IOrganizationProjectsTableSimpleCellProps & { children?: React.ReactNode }) {
    return (
        <SimpleTableCell key={props.cellKey} columnIndex={props.columnIndex} tableColumn={props.tableColumn}>
            <Tooltip overflowOnly={true}>
                <span className="text-ellipsis">{props.children}</span>
            </Tooltip>
        </SimpleTableCell>
    );
}

function OrganizationProjectsTableSimpleCellWithLink(
    props: IOrganizationProjectsTableSimpleCellProps & {
        url: string;
        label: string;
    }
) {
    const { url, label } = props;
    return (
        <OrganizationProjectsTableSimpleCell cellKey={props.cellKey} columnIndex={props.columnIndex} tableColumn={props.tableColumn}>
            <Link href={url} target="_blank" rel="noopener noreferrer" aria-label={label}>
                {label}
            </Link>
        </OrganizationProjectsTableSimpleCell>
    );
}

function DisplayDate(props: { date: Date }) {
    const { date } = props;
    return <time dateTime={date.toISOString()}>{date.toLocaleDateString()}</time>;
}

export class OrganizationProjectsTableComponent extends FilterablePivot<IOrganizationProjectsTableProps> {
    private readonly _itemProvider: IObservableArray<OrganizationProjectReference>;
    private readonly _selection = new ListSelection({ selectOnFocus: false, multiSelect: true });
    private readonly _showMultiDeleteButton = new ObservableValue<boolean>(false);
    private readonly _sortedTableItemProvider: ObservableValue<ArrayItemProvider<OrganizationProjectReference>> = new ObservableValue<
        ArrayItemProvider<OrganizationProjectReference>
    >(new ArrayItemProvider([]));

    private _columnSelected?: number;
    private _sortOrderSelected?: SortOrder;
    private _selectionLock: number = 0;

    public constructor(props: IOrganizationProjectsTableProps) {
        super(props);
        this._itemProvider = new ObservableArray<OrganizationProjectReference>(this.props.Projects);
        this._columnSelected = 1;
        this._sortOrderSelected = SortOrder.ascending;
    }

    public componentDidUpdate() {
        this._selection.subscribe(this._onSelectionChange);
        this.props.ClearSelection.subscribe(this._onClearSelection);
    }

    /***Filterable Pivot Methods */
    protected _getItemsName(): string {
        return AdminResources.Project;
    }

    protected _isContentReady(): boolean {
        return !this.props.IsInProgress && !this._isFiltering();
    }

    protected _getFilteredContentColumns(): ITableColumn<OrganizationProjectReference>[] {
        return this._columns;
    }

    protected _onRenderRow(index: number, item: OrganizationProjectReference, details: ITableRowDetails<OrganizationProjectReference>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        return this._getRenderElements(this._itemProvider.value, this._columns);
    }

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        return this._getRenderElements(resultItems, this._getFilteredContentColumns(), resultItems.length < 1);
    }

    protected _onFilterChanged(keyword: string): void {
        this._selection.clear();
        this.props.OnSearchProject(this.props.Projects, keyword, this.props.searchResult ? this.props.searchResult.keyword : "");
    }

    protected _isProjectScope(): boolean {
        return false;
    }

    protected _onExpandSearch(): void {}

    protected _isCollectionNavigationAllowed(): boolean {
        return false;
    }
    /***End Filterable Pivot Methods */

    protected _getRenderElements(
        items: OrganizationProjectReference[],
        columns: ITableColumn<OrganizationProjectReference>[],
        zeroFilteredData?: boolean
    ): JSX.Element {
        if (this._columnSelected != undefined && this._sortOrderSelected != undefined) {
            this._sortedTableItemProvider.value = new ArrayItemProvider(
                sortItems(this._columnSelected, this._sortOrderSelected, OrganizationProjectsTableComponent.sortFunctions, this._columns, items)
            );
        } else {
            this._sortedTableItemProvider.value = new ArrayItemProvider(items);
        }

        const totalProjectCount = this.props.Projects.length;
        const totalFilteredResultCount = this.props.searchResult.resultItems.length;

        // Disable the selection column when there is no project to select for multi-delete
        // Keep track of the lock count because selection is unlocked only if net count == 0
        if (!totalProjectCount || (this.props.searchResult.status === SearchStatus.Completed && !totalFilteredResultCount)) {
            if (!this._selectionLock) {
                this._selection.lock();
                this._selectionLock--;
            }
        } else {
            if (this._selectionLock < 0) {
                this._selection.unlock();
                this._selectionLock++;
            }
        }

        return (
            <div>
                <CustomHeader className="bolt-header-with-commandbar">
                    <HeaderTitleArea className="flex-row table-title-text">
                        {this._renderProjectAndFilterCount(totalProjectCount, totalFilteredResultCount)}
                    </HeaderTitleArea>
                    <Observer showMultiDeleteButton={this._showMultiDeleteButton}>
                        {(props: { showMultiDeleteButton: boolean }) => {
                            return props.showMultiDeleteButton ? (
                                <HeaderCommandBar items={this._setMultiDeleteButton} />
                            ) : (
                                <HeaderCommandBar items={this._setCreateProjectButton} />
                            );
                        }}
                    </Observer>
                </CustomHeader>
                <div className="flex-grow project-column table-minimum-card-height">
                    <Observer itemProvider={this._sortedTableItemProvider}>
                        {(observableProps: { itemProvider: ArrayItemProvider<OrganizationProjectReference> }) => (
                            <Table<OrganizationProjectReference>
                                behaviors={[this.sortingBehavior]}
                                itemProvider={observableProps.itemProvider}
                                columns={columns}
                                selectRowOnClick={false}
                                onActivate={this.props.OnRowClicked}
                                selection={this._selection}
                            />
                        )}
                    </Observer>
                    {zeroFilteredData && this._getZeroData()}
                </div>
            </div>
        );
    }

    private static renderProject(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return (
            <SimpleTableCell key={"row" + rowIndex + "col" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                <VssPersona
                    className="icon-medium-margin"
                    identityDetailsProvider={{
                        getIdentityImageUrl: (size: number): string | undefined => {
                            return tableItem.defaultTeamImageUrl && tableItem.defaultTeamImageUrl.length ? tableItem.defaultTeamImageUrl : undefined;
                        },
                        getDisplayName: (): string => {
                            return tableItem.name;
                        }
                    }}
                    size={"medium"}
                />
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis">{tableItem.name}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private static renderDescription(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return (
            <OrganizationProjectsTableSimpleCell cellKey={"row" + rowIndex + "col" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                {tableItem.description}
            </OrganizationProjectsTableSimpleCell>
        );
    }

    private static renderLastUpdateTime(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return (
            <OrganizationProjectsTableSimpleCell cellKey={"row" + rowIndex + "col" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                <DisplayDate date={tableItem.lastUpdateTime} />
            </OrganizationProjectsTableSimpleCell>
        );
    }

    private readonly renderProcess = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) => {
        const processTemplateName = tableItem.processTemplateName != undefined ? tableItem.processTemplateName : "";
        const processUrl = routeUrl(this.props.pageContext, OrganizationProjectsConstants.UrlRouterService, {
            _a: OrganizationProjectsConstants.routerWorkItem,
            "process-name": processTemplateName
        });

        return (
            <OrganizationProjectsTableSimpleCellWithLink
                cellKey={"row" + rowIndex + "col" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                url={processUrl}
                label={processTemplateName}
            />
        );
    };

    private readonly _columns: ITableColumn<OrganizationProjectReference>[] = [
        new ColumnSelect(),
        {
            id: "project",
            name: AdminResources.ProjectName,
            onSize: OrganizationProjectsTableComponent._onSizeColumn,
            width: new ObservableValue(-40),
            renderCell: OrganizationProjectsTableComponent.renderProject,
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "description",
            name: AdminResources.ProjectDescription,
            onSize: OrganizationProjectsTableComponent._onSizeColumn,
            width: new ObservableValue(-60),
            renderCell: OrganizationProjectsTableComponent.renderDescription,
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "lastUpdateTime",
            name: AdminResources.LastUpdatedDate,
            onSize: OrganizationProjectsTableComponent._onSizeColumn,
            width: new ObservableValue(-15),
            renderCell: OrganizationProjectsTableComponent.renderLastUpdateTime,
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "process",
            name: AdminResources.Process,
            onSize: OrganizationProjectsTableComponent._onSizeColumn,
            width: new ObservableValue(-15),
            renderCell: this.renderProcess,
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "visibility",
            name: AdminResources.ProjectVisibility,
            onSize: OrganizationProjectsTableComponent._onSizeColumn,
            width: new ObservableValue(-15),
            renderCell: OrganizationProjectsTableComponent.renderVisibility,
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        new ColumnMore<OrganizationProjectReference>((item: OrganizationProjectReference) => {
            return {
                id: "sub-menu",
                items: [
                    {
                        id: "editGroup",
                        iconProps: { iconName: "Edit" },
                        text: AdminResources.RenameProject,
                        disabled: this.props.IsInProgress || !item.hasRenamePermission,
                        onActivate: () => {
                            this.props.OnRenameProject(item);
                        }
                    },
                    {
                        id: "deleteGroup",
                        iconProps: { iconName: "Delete" },
                        text: AdminResources.DeleteProject,
                        disabled: this.props.IsInProgress || !item.hasDeletePermission,
                        onActivate: () => {
                            this.props.OnDeleteProject(item);
                        }
                    }
                ]
            };
        })
    ];

    private static renderVisibility(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return (
            <OrganizationProjectsTableSimpleCell cellKey={"row" + rowIndex + "col" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                {OrganizationProjectsTableComponent._getProjectVisibility(tableItem.visibility)}
            </OrganizationProjectsTableSimpleCell>
        );
    }

    private static _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<OrganizationProjectReference>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private sortingBehavior = new ColumnSorting<OrganizationProjectReference>((columnIndex: number, proposedSortOrder: SortOrder) => {
        this._columnSelected = columnIndex;
        this._sortOrderSelected = proposedSortOrder;
        this._selection.clear();
        this._sortedTableItemProvider.value = new ArrayItemProvider(
            sortItems(
                this._columnSelected,
                this._sortOrderSelected,
                OrganizationProjectsTableComponent.sortFunctions,
                this._columns,
                this.props.searchResult.status === SearchStatus.Completed ? this.props.searchResult.resultItems : this.props.Projects
            )
        );
    });

    private static sortFunctions = [
        null,
        // Sort on Project column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            return item1.name!.localeCompare(item2.name!, navigator.language);
        },
        // Sort on Description column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            var description1 = item1.description ? item1.description : "";
            var description2 = item2.description ? item2.description : "";
            return description1.localeCompare(description2, navigator.language);
        },
        // Sort on Last Updated column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            return item1.lastUpdateTime.getTime() > item2.lastUpdateTime.getTime() ? 1 : -1;
        },
        // Sort on Process column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            var process1 = item1.processTemplateName ? item1.processTemplateName : "";
            var process2 = item2.processTemplateName ? item2.processTemplateName : "";
            return process1.localeCompare(process2, navigator.language);
        },
        // Sort on Visibility column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            return OrganizationProjectsTableComponent._getProjectVisibility(item1.visibility).localeCompare(
                OrganizationProjectsTableComponent._getProjectVisibility(item2.visibility), navigator.language
            );
        },
        null
    ];

    private static _getProjectVisibility(visibility: ProjectVisibility): string {
        switch (visibility) {
            case ProjectVisibility.Organization:
                return AdminResources.OrganizationVisibilityTitle;
            case ProjectVisibility.Private:
                return AdminResources.PrivateVisibilityTitle;
            case ProjectVisibility.Public:
                return AdminResources.PublicVisibilityTitle;
            default:
                return AdminResources.SystemPrivate;
        }
    }

    private _renderProjectAndFilterCount(totalProjectCount: number, totalFilteredResultCount: number): JSX.Element {
        this.props.searchResult.status === SearchStatus.Completed &&
            announce(format(AdminResources.TableFilterResultsCount, totalFilteredResultCount));
        return (
            <PillGroup>
                <span className="text-ellipsis">{AdminResources.ProjectTotal}</span>
                <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                    {totalProjectCount}
                </Pill>
                {this.props.searchResult.status == SearchStatus.Completed && (
                    <>
                        <span className="text-ellipsis">{AdminResources.FilteredProjectTotal}</span>
                        <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                            {totalFilteredResultCount}
                        </Pill>
                    </>
                )}
            </PillGroup>
        );
    }

    private _setCreateProjectButton: IHeaderCommandBarItem[] = [
        {
            id: "addproject",
            className: "create-button",
            iconProps: { iconName: "Add" },
            ariaLabel: "add",
            onActivate: () => {
                this._selection.clear();
                this.props.OnCreateProject();
            },
            text: AdminResources.CreateProject,
            isPrimary: true,
            disabled: false
        }
    ];
    private _setMultiDeleteButton: IHeaderCommandBarItem[] = [
        {
            id: "deleteproject",
            className: "create-button",
            iconProps: { iconName: "Delete" },
            ariaLabel: "delete",
            onActivate: () => {
                this._onMultiDeleteClicked();
            },
            text: AdminResources.DeleteProjects,
            isPrimary: false,
            disabled: false
        }
    ];
    private _onMultiDeleteClicked = () => {
        var selectedProjectsToDelete: OrganizationProjectReference[] = [];
        if (this._selection.value.length > 0) {
            for (const range of this._selection.value) {
                for (let i = range.beginIndex; i <= range.endIndex; i++) {
                    const selectedItem = this._sortedTableItemProvider.value.value[i];
                    if (selectedItem) {
                        selectedProjectsToDelete.push(selectedItem);
                    }
                }
            }
        }
        this.props.onMultiProjectDelete(selectedProjectsToDelete);
    };

    private _onSelectionChange = () => {
        this._showMultiDeleteButton.value = this._selection.selectedCount > 0 && this.props.Projects.length > 0;
    };

    private _onClearSelection = (value: boolean) => {
        if (value) {
            this._selection.clear();
        }
    };
}

Components.add("organizationProjectsTable", OrganizationProjectsTableComponent);
